/**
 * Admin — Vérifications covoiturage Nold Go.
 * Liste les conducteurs qui ont uploadé permis + carte grise (+ pièce d'identité opt).
 * L'admin peut consulter chaque document et approuver / rejeter avec motif.
 */
import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Check, X, ExternalLink, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";

const TAB_LABELS = { pending: "En attente", approved: "Approuvés", rejected: "Rejetés" };

export default function CarpoolVerifAdmin() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null); // { userId, kind, url, contentType }
  const [busyId, setBusyId] = useState(null);

  const load = async (t = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/carpool/admin/verifications`, { params: { status: t } });
      setItems(data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  // Doc protégé (admin auth requise) → fetch en blob puis URL locale pour <img> / <iframe>.
  const viewDoc = async (userId, kind) => {
    try {
      const r = await api.get(`/carpool/admin/verifications/${userId}/document/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setViewing({ userId, kind, url, contentType: r.data.type });
    } catch (e) { toast.error(e?.response?.data?.detail || "Document introuvable"); }
  };

  const approve = async (userId) => {
    if (!window.confirm("Approuver ce conducteur ? Un badge « Vérifié » sera affiché sur tous ses trajets.")) return;
    setBusyId(userId);
    try {
      await api.post(`/carpool/admin/verifications/${userId}`, { approve: true });
      toast.success("Conducteur approuvé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };
  const reject = async (userId) => {
    const reason = window.prompt("Motif de rejet (visible par le conducteur) :");
    if (!reason || !reason.trim()) return;
    setBusyId(userId);
    try {
      await api.post(`/carpool/admin/verifications/${userId}`, { approve: false, reason: reason.trim() });
      toast.success("Conducteur rejeté");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-carpool-verif-section" className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#059669] flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-white" /></div>
        <div>
          <h2 className="font-head text-2xl font-black text-[#1A1A1A]">Vérifications Conducteurs Covoiturage</h2>
          <p className="text-xs text-[#75807A]">Approuvez ou rejetez les documents (permis, carte grise, pièce d'identité) uploadés par les conducteurs.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E2DFD8]">
        {Object.entries(TAB_LABELS).map(([k, label]) => (
          <button key={k} data-testid={`carpool-verif-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 h-10 text-sm font-bold transition-colors border-b-2 -mb-px ${tab === k ? "text-[#F26419] border-[#F26419]" : "text-[#75807A] border-transparent hover:text-[#1A1A1A]"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="p-10 text-center text-[#75807A]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Chargement…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] p-8 text-center text-[#75807A]">
          Aucun conducteur dans cette catégorie.
        </div>
      ) : items.map((u) => (
        <div key={u.id} data-testid={`carpool-verif-item-${u.id}`} className="bg-white rounded-2xl p-4 border border-[#E2DFD8]">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="font-bold text-[#1A1A1A]">{u.name || u.email}</div>
              <div className="text-xs text-[#75807A] mt-1">{u.email} · {u.phone || "sans téléphone"}</div>
              <div className="text-xs text-[#3B423E] mt-1">Véhicule : <b>{u.carpool_vehicle_model || "—"}</b> · Plaque : <b>{u.carpool_vehicle_plate || "—"}</b></div>
              <div className="text-[10px] text-[#75807A] mt-1">Soumis : {u.carpool_verification_submitted_at ? new Date(u.carpool_verification_submitted_at).toLocaleString("fr-FR") : "—"}
                {u.carpool_verified_at && ` · Approuvé le ${new Date(u.carpool_verified_at).toLocaleString("fr-FR")}`}
              </div>
              {u.carpool_verification_rejected_reason && <div className="text-xs text-[#DC2626] mt-1">Motif rejet : {u.carpool_verification_rejected_reason}</div>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid={`carpool-verif-view-license-${u.id}`} onClick={() => viewDoc(u.id, "license")}
                className="px-3 py-1.5 rounded-full bg-white border border-[#E2DFD8] text-[#1A1A1A] text-xs font-bold hover:bg-[#F0EBE1] inline-flex items-center gap-1">
                <FileText className="w-3 h-3" /> Permis
              </button>
              <button data-testid={`carpool-verif-view-registration-${u.id}`} onClick={() => viewDoc(u.id, "registration")}
                className="px-3 py-1.5 rounded-full bg-white border border-[#E2DFD8] text-[#1A1A1A] text-xs font-bold hover:bg-[#F0EBE1] inline-flex items-center gap-1">
                <FileText className="w-3 h-3" /> Carte grise
              </button>
              <button data-testid={`carpool-verif-view-id-${u.id}`} onClick={() => viewDoc(u.id, "id")}
                className="px-3 py-1.5 rounded-full bg-white border border-[#E2DFD8] text-[#1A1A1A] text-xs font-bold hover:bg-[#F0EBE1] inline-flex items-center gap-1">
                <FileText className="w-3 h-3" /> Pièce ID
              </button>
              {tab === "pending" && (
                <>
                  <button data-testid={`carpool-verif-approve-${u.id}`} onClick={() => approve(u.id)} disabled={busyId === u.id}
                    className="px-3 py-1.5 rounded-full bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold disabled:opacity-50 inline-flex items-center gap-1">
                    <Check className="w-3 h-3" /> Approuver
                  </button>
                  <button data-testid={`carpool-verif-reject-${u.id}`} onClick={() => reject(u.id)} disabled={busyId === u.id}
                    className="px-3 py-1.5 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold disabled:opacity-50 inline-flex items-center gap-1">
                    <X className="w-3 h-3" /> Rejeter
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Modal viewer */}
      {viewing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => { URL.revokeObjectURL(viewing.url); setViewing(null); }}
          data-testid="carpool-verif-doc-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-[#1A1A1A] uppercase text-xs tracking-widest">
                {viewing.kind === "license" ? "Permis de conduire" : viewing.kind === "registration" ? "Carte grise" : "Pièce d'identité"}
              </div>
              <button onClick={() => { URL.revokeObjectURL(viewing.url); setViewing(null); }}
                className="text-[#75807A] hover:text-[#1A1A1A] p-1" data-testid="carpool-verif-doc-close">✕</button>
            </div>
            {viewing.contentType?.includes("pdf") ? (
              <iframe src={viewing.url} title="doc" className="w-full h-[70vh] rounded-lg" />
            ) : (
              <img src={viewing.url} alt="doc" className="w-full h-auto rounded-lg" />
            )}
            <div className="mt-3 text-center">
              <a href={viewing.url} download={`carpool-${viewing.kind}-${viewing.userId}`}
                className="text-xs font-bold text-[#F26419] hover:underline inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Télécharger l'original
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
